import { BarChart3, Plus, Table2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ToggleGroup,
  ToggleGroupItem,
} from "@nexus/ui";
import type { MetricCatalogEntry } from "../../lib/metricCatalog.js";
import {
  LOCKED_CHART_METRIC_ID,
  MAX_CHART_METRICS,
  type ChartMetricSelection,
  type ChartViewMode,
} from "../../stores/overviewFilters.store.js";
import { resolveMetrics } from "./chartModel.js";
import { MetricPill } from "./MetricPill.js";

interface MetricSelectorToolbarProps {
  chartMetrics: ChartMetricSelection[];
  catalog: MetricCatalogEntry[];
  catalogById: Record<string, MetricCatalogEntry>;
  sortMetricId: string;
  viewMode: ChartViewMode;
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
  onToggleSort: (id: string) => void;
  onSetViewMode: (mode: ChartViewMode) => void;
}

const VIEW_MODES: Array<{ value: ChartViewMode; labelKey: string; defaultLabel: string; Icon: typeof BarChart3 }> = [
  { value: "bars", labelKey: "overview.chart.viewBars", defaultLabel: "Bars", Icon: BarChart3 },
  { value: "table", labelKey: "overview.chart.viewTable", defaultLabel: "Table", Icon: Table2 },
];

/** Metric-selector pills + "+" add picker (left) and the Bars/Table toggle (right). */
export function MetricSelectorToolbar({
  chartMetrics,
  catalog,
  catalogById,
  sortMetricId,
  viewMode,
  onAdd,
  onRemove,
  onToggleSort,
  onSetViewMode,
}: MetricSelectorToolbarProps) {
  const { t } = useTranslation();
  const metrics = resolveMetrics(chartMetrics, catalogById);
  const selectedIds = new Set(chartMetrics.map((m) => m.id));
  const available = catalog.filter((e) => !selectedIds.has(e.id));
  const atCap = chartMetrics.length >= MAX_CHART_METRICS;

  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-wrap items-center gap-3">
        {metrics.map((m) => (
          <MetricPill
            key={m.id}
            metric={m}
            canRemove={m.id !== LOCKED_CHART_METRIC_ID}
            isActiveSort={m.id === sortMetricId}
            onToggleSort={onToggleSort}
            onRemove={onRemove}
          />
        ))}

        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={t("overview.chart.addMetric", "Add metric")}
              data-testid="add-metric"
              disabled={atCap || available.length === 0}
              className="flex size-[34px] items-center justify-center rounded-[10px] border border-dashed border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Plus className="size-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="max-h-[320px] w-[220px] overflow-y-auto p-1">
            {available.map((e) => (
              <Button
                key={e.id}
                variant="ghost"
                size="sm"
                data-testid={`add-metric-option-${e.id}`}
                className="w-full justify-start gap-2 font-normal"
                onClick={() => onAdd(e.id)}
              >
                <span
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ background: e.colorVar }}
                  aria-hidden
                />
                {e.label}
              </Button>
            ))}
          </PopoverContent>
        </Popover>
      </div>

      <ToggleGroup
        type="single"
        variant="pill"
        value={viewMode}
        onValueChange={(v) => v && onSetViewMode(v as ChartViewMode)}
        data-testid="view-mode-toggle"
      >
        {VIEW_MODES.map(({ value, labelKey, defaultLabel, Icon }) => (
          <ToggleGroupItem key={value} value={value} aria-label={t(labelKey, defaultLabel)} className="gap-1.5 text-[12px]">
            <Icon className="size-3.5" />
            {t(labelKey, defaultLabel)}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}
